import { ClassifiedList } from '~/models/classified-list/types'
import ClassifiedListsService from '~/services/ClassifiedListsService'
import SearchBucketService from '~/services/search-bucket/SearchBucketService'
import { PAGE_NS } from '~/store/modules/shared/page/state'
import { ADD_SUBSCRIBABLE_SEARCH_IDS } from '~/store/modules/shared/parking/searchSubscriptions/mutation-types'
import { SEARCH_SUBSCRIPTION_NS } from '~/store/modules/shared/parking/searchSubscriptions/state'
import { ParkingState } from '~/store/modules/shared/parking/state'
import { ActionTreeWithRootState } from '~/store/types'

import {
  SET_GROUPS,
  SET_CLASSIFIEDS_LIST,
  SET_SEARCHES,
  SET_ERROR,
  REMOVE_SEARCH,
  ADD_TO_SEARCHES,
  REMOVE_CLASSIFIED,
  SET_LOADING,
  SET_RECENT,
  CLEAR_NOTIFICATION_FOR_INDEX,
  SET_CLASSIFIED_ORDER,
  SET_CLASSIFIEDS_WITH_NOTIFICATIONS,
  SHOW_PARKING,
  HIDE_PARKING,
  EDIT_CLASSIFIED_LIST,
  FETCH_CLASSIFIED_LISTS_CLASSIFIEDS_BY_ID,
  REMOVE_CLASSIFIED_LIST,
  SET_CLASSIFIED_LIST_ACTION_BUTTONS_LOADING,
  SET_CLASSIFIED_LIST_LOADING,
  DELETE_CLASSIFIED_FROM_CLASSIFIED_LIST,
  CLEAR_PARKING_NOTIFICATIONS,
  REMOVE_LIST_FROM_FAVORITES,
  SET_NOTES_FOR_CLASSIFIED,
  ADD_FAVORITE_CLASSIFIED
} from './mutation-types'
import ClassifiedBucketService from '~/services/classified/buckets/ClassifiedBucketService'
import { ClassifiedBucketType } from '~/models/classified/types'
import { SearchesBucketType } from '~/models/search-bucket/types'
import { ClassifiedsFavoritesService } from '~/services/ClassifiedsFavoritesService'
import { ClassifiedNote } from '~/models/classified/note'

export default {
  async fetchClassifieds(
    { commit },
    {
      silent = false,
      sort,
      category
    }: { silent: boolean; sort: string; category: number }
  ) {
    try {
      if (!silent) {
        commit(SET_LOADING, true)
      }

      const groups = await this.$dep(
        ClassifiedsFavoritesService
      ).getFavoriteClassifieds(category, sort)
      commit(SET_GROUPS, groups)
      if (!silent) {
        commit(SET_LOADING, false)
      }
    } catch (e) {
      commit(SET_ERROR, true)
      if (!silent) {
        commit(SET_LOADING, false)
      }
      this.$logger.captureError(e)
    }
  },
  async fetchSearches({ commit }) {
    try {
      commit(SET_LOADING, true)

      const searches = await this.$dep(SearchBucketService).getBucketSearches(
        SearchesBucketType.FAVORITE
      )
      commit(SET_SEARCHES, searches)
      commit(
        `${SEARCH_SUBSCRIPTION_NS}/${ADD_SUBSCRIBABLE_SEARCH_IDS}`,
        this.$dep(SearchBucketService).filterSubscribableSearchIds(searches),
        { root: true }
      )
      commit(SET_LOADING, false)
    } catch (e) {
      commit(SET_ERROR, true)
      commit(SET_LOADING, false)
      this.$logger.captureError(e)
    }
  },
  async fetchRecent({ commit }) {
    try {
      commit(SET_LOADING, true)
      const searches = await this.$dep(SearchBucketService).getBucketSearches(
        SearchesBucketType.RECENT
      )

      commit(SET_RECENT, {
        searches
      })
    } catch (e) {
      commit(SET_ERROR, true)
      this.$logger.captureError(e)
    } finally {
      commit(SET_LOADING, false)
    }
  },
  async fetchClassifiedsList({ commit }, silent: boolean = false) {
    try {
      if (!silent) {
        commit(SET_LOADING, true)
      }

      const rows = await this.$dep(
        ClassifiedBucketService
      ).getBucketClassifieds(ClassifiedBucketType.FAVORITE)

      const orderArray: number[] = []
      rows.forEach(row => orderArray.push(row.id))

      const withNotifications: number[] = []
      rows.forEach(row => {
        if (row.notifications.length) {
          withNotifications.push(row.id)
        }
        return null
      })

      if (withNotifications.length) {
        commit(SET_CLASSIFIEDS_WITH_NOTIFICATIONS, withNotifications)

        if (process.client) {
          window.parent.postMessage(
            {
              event_id: 'clear_parking_notification'
            },
            '*'
          )
        }
      }

      commit(SET_CLASSIFIED_ORDER, orderArray)
      commit(SET_CLASSIFIEDS_LIST, rows)
      if (!silent) {
        commit(SET_LOADING, false)
      }
    } catch (e) {
      commit(SET_ERROR, true)
      if (!silent) {
        commit(SET_LOADING, false)
      }
      this.$logger.captureError(e)
    }
  },
  async removeFavoriteClassified(
    { commit },
    { id, category, listId }: { id: number; category: number; listId?: string }
  ) {
    try {
      await this.$dep(
        ClassifiedsFavoritesService
      ).removeClassifiedFromFavorites(id)
      commit(REMOVE_CLASSIFIED, { id, category, listId })
    } catch (e) {
      commit(SET_ERROR, true)
      throw e
    }
  },
  async addFavoriteClassified(
    { commit },
    { listId, id }: { listId: number; id: number }
  ) {
    try {
      await this.$dep(ClassifiedsFavoritesService).addClassifiedToFavorites(id)
      commit(ADD_FAVORITE_CLASSIFIED, { listId, id })
    } catch (e) {
      commit(SET_ERROR, true)
      throw e
    }
  },
  async removeFavoriteSearch({ commit }, id: number | string) {
    try {
      await this.$dep(SearchBucketService).removeSearchFromBucket(
        SearchesBucketType.FAVORITE,
        id
      )
      commit(REMOVE_SEARCH, id)
    } catch (e) {
      commit(SET_ERROR, true)
      this.$logger.captureError(e)
    }
  },
  async addFavoriteSearch({ commit }, searchId) {
    try {
      commit(SET_LOADING, true)

      const { status } = await this.$dep(
        SearchBucketService
      ).addSearchToFavorites({ searchId })

      if (status === 200) {
        commit(ADD_TO_SEARCHES, searchId)
      }

      commit(SET_LOADING, false)
    } catch (e) {
      commit(SET_ERROR, true)
      commit(SET_LOADING, false)
      this.$logger.captureError(e)
    }
  },
  clearNotificationsForIndex({ state, commit, dispatch }, index) {
    commit(CLEAR_NOTIFICATION_FOR_INDEX, index)
    if (state.groups?.filters?.categories) {
      const noNotifications = !state.groups.filters.categories.some(
        c => c.notificationsCount && c.notificationsCount > 0
      )

      if (noNotifications) {
        dispatch('clearParkingNotifications')
      }
    }
  },
  clearParkingNotifications({ commit }) {
    commit(CLEAR_PARKING_NOTIFICATIONS)

    if (typeof window === 'undefined') {
      return
    }

    if (window.parent && window.parent !== window) {
      // remove from iframe parent header
      window.parent.postMessage(
        {
          event_id: 'clear_parking_notification'
        },
        '*'
      )
    } else {
      // remove from car2 header
      const el = document.querySelector(
        '.for-parking.ignore-for-mobile-menu .has-notifications'
      )
      if (el) {
        // @ts-ignore
        el.parentNode.removeChild(el)
      }
    }
  },
  async fetchListClassifiedsById({ commit, state }, { listId, page = 1 }) {
    if (!state.loading) {
      commit(SET_CLASSIFIED_LIST_LOADING, true)
    }
    try {
      const res = await this.$dep(
        ClassifiedListsService
      ).fetchListClassifiedsById(listId, page)
      commit(FETCH_CLASSIFIED_LISTS_CLASSIFIEDS_BY_ID, res)
    } catch (e) {
      this.$logger.captureError(e)
      throw e
    } finally {
      commit(SET_CLASSIFIED_LIST_LOADING, false)
    }
  },
  async removeClassifiedFromList(
    { dispatch, commit, state },
    { listId, classifiedId }
  ): Promise<{ status: number; message: string } | undefined> {
    commit(SET_CLASSIFIED_LIST_ACTION_BUTTONS_LOADING, true)
    try {
      const res: {
        status: number
        message: string
      } = await this.$dep(ClassifiedListsService).removeClassifiedFromList(
        listId,
        classifiedId
      )

      const page = Math.ceil(
        (state.classifiedsPagination.total - 1) /
          state.classifiedsPagination.perPage
      )

      dispatch('fetchListClassifiedsById', { listId, page })
      this.$snackbar.success(
        // @ts-ignore
        this.$i18n.t('classified deleted from list successfully')
      )
      commit(DELETE_CLASSIFIED_FROM_CLASSIFIED_LIST, listId)
      return res
    } catch (e) {
      this.$logger.captureError(e)
    } finally {
      commit(SET_CLASSIFIED_LIST_ACTION_BUTTONS_LOADING, false)
    }
  },
  async editList({ commit }, { listId, listName }) {
    commit(SET_CLASSIFIED_LIST_ACTION_BUTTONS_LOADING, true)
    try {
      const list = await this.$dep(ClassifiedListsService).editList(
        listId,
        listName
      )
      commit(EDIT_CLASSIFIED_LIST, list)
      this.$snackbar.success(
        // @ts-ignore
        this.$i18n.t('list updated successfully')
      )
    } catch (e) {
      this.$logger.captureError(e)
      throw e
    } finally {
      commit(SET_CLASSIFIED_LIST_ACTION_BUTTONS_LOADING, false)
    }
  },
  async removeListFromFavorites(
    { dispatch, commit, state },
    list: ClassifiedList
  ) {
    commit(SET_CLASSIFIED_LIST_ACTION_BUTTONS_LOADING, true)
    try {
      await this.$dep(ClassifiedListsService).removeListFromFavorites(
        list.publicId
      )
      const filteredList = state.classifiedLists.filter(
        l => l.publicId !== list.publicId
      )

      if (state?.selectedList?.publicId === list.publicId) {
        await dispatch('fetchClassifiedsForTheFirstListItem', {
          filteredList,
          list
        })
      } else {
        commit(REMOVE_LIST_FROM_FAVORITES, list.publicId)
        commit(SET_CLASSIFIED_LIST_ACTION_BUTTONS_LOADING, false)
      }

      this.$snackbar.success(
        // @ts-ignore
        this.$i18n.t('list removed successfully')
      )
    } catch (e) {
      this.$logger.captureError(e)
    } finally {
      commit(SET_CLASSIFIED_LIST_ACTION_BUTTONS_LOADING, false)
    }
  },
  async fetchClassifiedsForTheFirstListItem(
    { commit },
    { filteredList, list }
  ) {
    if (filteredList.length > 0) {
      const firstListItem: ClassifiedList = filteredList[0]
      try {
        const res = await this.$dep(
          ClassifiedListsService
        ).fetchListClassifiedsById(firstListItem.publicId, 1)
        commit(REMOVE_CLASSIFIED_LIST, {
          listIdToRemove: list.publicId,
          classifieds: res.classifieds,
          firstListItem
        })
      } catch (error) {
        this.$logger.captureError(error)
      }
    } else {
      commit(REMOVE_CLASSIFIED_LIST, { listIdToRemove: list.publicId })
    }
  },
  showParking({ commit }) {
    commit(SHOW_PARKING)
    this.$cookies.set('parking_open', 1)

    // @ts-ignore
    window.PARKING_OPEN = true
  },
  hideParking({ commit, dispatch }) {
    commit(HIDE_PARKING)

    const event = document.createEvent('Event')
    event.initEvent('park-modal-closed', true, true)
    document.dispatchEvent(event)
    dispatch(`${PAGE_NS}/removeBodyClass`, 'modal-open', { root: true })
    document.body.style.marginRight = ''
    this.$cookies.delete('parking_open')
  },
  setNotesForClassified(
    { commit },
    {
      note,
      classifiedId
    }: {
      note: ClassifiedNote | null
      classifiedId: number
    }
  ) {
    commit(SET_NOTES_FOR_CLASSIFIED, { note, clsfdId: classifiedId })
  }
} as ActionTreeWithRootState<ParkingState>
